import { secondsToTimeString } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { useEventCallback } from '@mui/material';
import { SubtenantDto } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useMemo } from 'react';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { DataGrid } from '@lmt/unicorn-shared/src/app/components/common/dataGrid/DataGrid';
import {
  DXG,
  RowClickEvent,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';

type Props = {
  noResults: boolean;
  data: SubtenantDto[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
};

const TableKeys: { [dictionary: string]: string } = {
  name: 'name',
  code: 'code',
  workingTimeStart: 'working_time_start',
  workingTimeEnd: 'working_time_end',
  actions: 'actions',
};

export const SubtenantsTable: FC<Props> = React.memo(
  function SubtenantsTable(props) {
    const i18n = useScopedTranslation('subtenants.list');
    const claims = useClaims();

    const onRowClick = useEventCallback((e: RowClickEvent) => {
      props.onEdit(e.data.id);
    });

    const columns = useMemo(
      () => [
        {
          key: TableKeys.name,
          caption: i18n.t(`columns.${TableKeys.name}` as any),
          cellRender: (cell: any) => cell.data.name,
        },
        {
          key: TableKeys.code,
          caption: i18n.t(`columns.${TableKeys.code}` as any),
          cellRender: (cell: any) => cell.data.code,
        },
        {
          key: TableKeys.workingTimeStart,
          caption: i18n.t(`columns.${TableKeys.workingTimeStart}` as any),
          cellRender: (cell: any) =>
            secondsToTimeString(cell.data.workingTimeStartInSeconds),
        },
        {
          key: TableKeys.workingTimeEnd,
          caption: i18n.t(`columns.${TableKeys.workingTimeEnd}` as any),
          cellRender: (cell: any) =>
            secondsToTimeString(cell.data.workingTimeEndInSeconds),
        },
        {
          key: TableKeys.actions,
          caption: '',
          width: '60px',
          cellRender: (cell: any) => (
            <div onClick={(event: any) => event.stopPropagation()}>
              {claims.permissions.deleteSubtenants && (
                <TableDotMenu
                  options={[
                    {
                      title: i18n.t('options.edit'),
                      onClick: () => props.onEdit(cell.data.id),
                    },
                    {
                      title: i18n.t('options.delete'),
                      onClick: () => props.onDelete(cell.data.id),
                      isDeleteItem: true,
                      isUnderLine: true,
                    },
                  ]}
                />
              )}
            </div>
          ),
        },
      ],
      [],
    );

    return (
      <DataGrid
        dataSource={props.data}
        enableScrollTop={true}
        sorting={{ mode: 'none' }}
        onRowClick={onRowClick}
        columns={columns}
      />
    );
  },
);
